import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    AutoSizer,
    CellMeasurer,
    CellMeasurerCache,
    List,
    ListRowProps,
    WindowScroller,
} from 'react-virtualized';
import 'react-virtualized/styles.css';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

import Switcher from '../../../../../components/common/switcher/Switcher';
import ProductItem from '../productItem/ProductItem';
import ExportProject from '../../../../../components/projects/projectActions/exportProject/ExportProject';
import { useModalState } from '../../../../../hooks/modalState';
import { blueDark01, blueLink } from '../../../../../styles/colors';
import { Text, InvisibleButton } from '../../../../../styles/common.styles';
import { DivisionsContainer } from '../ProductsTab.styles';
import {
    DivisionItemContainer,
    DivisionItemWrapper,
    DivisionTitle,
    DivisionTitleContainer,
    ExportDivisionImage,
    OpenCloseDivisionButton,
} from '../divisionItem/DivisionItem.styles';
import {
    MFRTitleContainer,
    ProductCheckbox,
    ProductScrollableContainer,
    SectionItemContainer,
    SectionItemDivider,
    SectionItemTitleContainer,
    SectionItemWrapper,
    SectionTitle,
} from '../sectionItem/SectionItem.styles';
import { ExportProjectType } from '../../../../../models/project/enums';
import { GetProjectInformationResponse } from '../../../../../redux/actions/projectActions/types';
import { ProjectReferences } from '../../../../../models/project/types';
import { ProductReferences } from '../../../../../redux/actions/productActions/types';
import { BrandCompany, Product } from '../../../../../models/product/types';
import { Division, Section } from '../../../../../models/productDivision/types';
import { buildVirtualRows, VirtualRow } from './productRows';

interface Props {
    divisions: Array<Division<Product>>;
    references: ProductReferences | null;
    projectReferences: ProjectReferences | null;
    projectInfo: GetProjectInformationResponse | null;
    editable: boolean;
    brandsInfo: Array<BrandCompany>;
    installedTab?: boolean;
    filterModalOpened?: boolean;
    selectedProducts?: Product[];
    handleClicked: (id: string) => void;
    handleSectionOpen: (divisionId: string, sectionId: string) => void;
    updateDivisions: (product: Product, isInstalled?: boolean) => void;
    handleDeleteProduct: (product: Product) => void;
    handleSelectProduct: (event: CheckboxChangeEvent, product: Product) => void;
}

// Bridges a row's real DOM height to react-virtualized's CellMeasurer. It calls
// `measure()` whenever the observed content resizes (inline edit validation,
// tags added/removed, etc.) so cached row heights stay accurate after mount.
const MeasuredRow: React.FC<{ measure: () => void; children: React.ReactNode }> = ({
    measure,
    children,
}) => {
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const element = ref.current;
        if (!element || typeof ResizeObserver === 'undefined') {
            measure();
            return;
        }
        const observer = new ResizeObserver(() => measure());
        observer.observe(element);
        return () => observer.disconnect();
    }, [measure]);

    return <div ref={ref}>{children}</div>;
};

const DivisionHeaderRow: React.FC<{
    division: Division<Product>;
    installedTab?: boolean;
    projectInfo: GetProjectInformationResponse | null;
    projectReferences: ProjectReferences | null;
    handleClicked: (id: string) => void;
    handleSectionOpen: (divisionId: string, sectionId: string) => void;
}> = ({ division, installedTab, projectInfo, projectReferences, handleClicked, handleSectionOpen }) => {
    const { isOpen, onToggle } = useModalState();

    const checkWhatActionNeeded = () => {
        if (!division.clicked) {
            return true;
        }
        for (const section of division.sections) {
            if (!section.clicked) {
                return true;
            }
        }
        return false;
    };

    const openCloseDivision = () => {
        const action = checkWhatActionNeeded();
        if (!division.sections?.length) {
            return;
        }
        handleClicked(division.id);
        division.clicked = action;
        for (const section of division.sections) {
            handleSectionOpen(division.id, section.id);
            section.clicked = action;
        }
    };

    return (
        <DivisionItemWrapper className={division.clicked ? 'clicked' : ''}>
            <DivisionItemContainer>
                <DivisionTitleContainer>
                    <Switcher
                        id={division.id}
                        clicked={division.clicked}
                        handleSwitch={handleClicked}
                        width={24}
                        height={24}
                        onClick={() => {
                            handleClicked(division.id);
                        }}
                    />
                    <DivisionTitle
                        className="division-id hover"
                        onClick={() => {
                            handleClicked(division.id);
                        }}
                    >
                        {division.id[0] + division.id[1]}
                    </DivisionTitle>
                    <DivisionTitle
                        className="hover"
                        onClick={() => {
                            handleClicked(division.id);
                        }}
                    >
                        {division.name}
                    </DivisionTitle>
                    <Text
                        weight={400}
                        size={20}
                        height={24}
                        color={blueDark01}
                        className="hover"
                        onClick={() => {
                            handleClicked(division.id);
                        }}
                    >
                        (
                        {division.sections.reduce((agg, item) => {
                            agg += item.products.length;
                            return agg;
                        }, 0)}
                        )
                    </Text>
                    <InvisibleButton>
                        <OpenCloseDivisionButton
                            onClick={openCloseDivision}
                            className="closeOpenDivision"
                        >
                            <Text weight={400} size={12} height={14} color={blueLink}>
                                Open / close all
                            </Text>
                        </OpenCloseDivisionButton>
                    </InvisibleButton>
                </DivisionTitleContainer>
                {!installedTab && !projectInfo?.isTemplate && (
                    <ExportDivisionImage
                        src="src/assets/images/file-division-export.svg"
                        title="Export division"
                        preview={false}
                        onClick={onToggle}
                    />
                )}
            </DivisionItemContainer>
            {isOpen && (
                <ExportProject
                    isOpen={isOpen}
                    closeModal={onToggle}
                    project={projectInfo}
                    division={division}
                    projectReferences={projectReferences}
                    wrapLinesInDropdown={true}
                    options={[
                        {
                            id: ExportProjectType.DIVISION_ALL_PRODUCTS.toString(),
                            name: `${division.name}, all products`,
                        },
                        {
                            id: ExportProjectType.DIVISION_BASIS_DESIGN.toString(),
                            name: `${division.name}, basis of design only`,
                        },
                        {
                            id: ExportProjectType.INSTALLED_PRODUCTS_DIVISION_ALL_PRODUCTS.toString(),
                            name: `Installed products - ${division.name}, all products`,
                        },
                        {
                            id: ExportProjectType.INSTALLED_PRODUCTS_DIVISION_BASIS_DESIGN.toString(),
                            name: `Installed products - ${division.name}, basis of design only`,
                        },
                    ]}
                    reload={() => {}}
                />
            )}
        </DivisionItemWrapper>
    );
};

const SectionHeaderRow: React.FC<{
    section: Section<Product>;
    onToggle: (sectionId: string) => void;
}> = ({ section, onToggle }) => (
    <>
        <SectionItemDivider />
        <SectionItemWrapper>
            <SectionItemContainer>
                <SectionItemTitleContainer
                    onClick={() => {
                        onToggle(section.id);
                    }}
                >
                    <Switcher
                        id={section.id}
                        clicked={section.clicked}
                        handleSwitch={() => onToggle(section.id)}
                        width={20}
                        height={20}
                        small
                    />
                    <SectionTitle className="section-id hover">{section.id}</SectionTitle>
                    <SectionTitle className="hover">{section.name}</SectionTitle>{' '}
                    <Text weight={400} size={16} height={20} color={blueDark01} className="hover">
                        ({section.products.length})
                    </Text>
                </SectionItemTitleContainer>
            </SectionItemContainer>
        </SectionItemWrapper>
    </>
);

const VirtualizedDivisions: React.FC<Props> = (props) => {
    const {
        divisions,
        brandsInfo,
        references,
        projectReferences,
        projectInfo,
        editable,
        installedTab,
        filterModalOpened,
        selectedProducts,
        handleClicked,
        handleSectionOpen,
        updateDivisions,
        handleDeleteProduct,
        handleSelectProduct,
    } = props;

    // Rebuilt every render so it always reflects the latest expand/collapse and
    // product state, even when the parent mutates `clicked` in place. Building the
    // (visible-only) row array is cheap; the expensive grouping is cached by ref.
    const rows = buildVirtualRows(divisions, brandsInfo);
    const rowsRef = useRef(rows);
    rowsRef.current = rows;

    const listRef = useRef<List | null>(null);
    const cache = useMemo(
        () =>
            new CellMeasurerCache({
                fixedWidth: true,
                defaultHeight: 140,
                keyMapper: (rowIndex) => rowsRef.current[rowIndex]?.key ?? rowIndex,
            }),
        [],
    );

    // When the set of rows changes structurally (expand/collapse/add/delete or a
    // tab/filter switch), tell the List to recompute positions. Per-key cached
    // heights are preserved via keyMapper; in-place height changes are handled by
    // MeasuredRow's ResizeObserver.
    const signature = `${rows.length}:${rows[0]?.key ?? ''}:${rows[rows.length - 1]?.key ?? ''}`;
    useEffect(() => {
        listRef.current?.recomputeRowHeights();
    }, [signature]);

    const renderRowContent = useCallback(
        (row: VirtualRow) => {
            switch (row.type) {
                case 'division':
                    return (
                        <DivisionHeaderRow
                            division={row.division}
                            installedTab={installedTab}
                            projectInfo={projectInfo}
                            projectReferences={projectReferences}
                            handleClicked={handleClicked}
                            handleSectionOpen={handleSectionOpen}
                        />
                    );
                case 'section':
                    return (
                        <SectionHeaderRow
                            section={row.section}
                            onToggle={(sectionId) => handleSectionOpen(row.division.id, sectionId)}
                        />
                    );
                case 'group':
                    return (
                        <ProductScrollableContainer>
                            <SectionItemDivider />
                            <MFRTitleContainer className={row.groupIndex ? 'sec' : ''}>
                                <SectionTitle className="section-id mfr">
                                    {row.group.number}
                                </SectionTitle>
                                <SectionTitle className="mfr">{`${row.group.label}`}</SectionTitle>{' '}
                                <Text
                                    weight={400}
                                    size={14}
                                    height={18}
                                    color={blueDark01}
                                    className="hover"
                                >
                                    ({row.group.products.length})
                                </Text>
                            </MFRTitleContainer>
                            <SectionItemDivider />
                        </ProductScrollableContainer>
                    );
                case 'product':
                    return (
                        <ProductScrollableContainer>
                            <ProductCheckbox
                                checked={selectedProducts?.some(
                                    (p) =>
                                        (p.projectProductId ?? p.id) ===
                                        (row.product.projectProductId ?? row.product.id),
                                )}
                                onChange={(e) => handleSelectProduct(e, row.product)}
                            />
                            <ProductItem
                                filterModalOpened={filterModalOpened}
                                previousProduct={row.previousProduct}
                                product={row.product}
                                references={references}
                                divider={row.divider}
                                projectInfo={projectInfo}
                                updateDivisions={updateDivisions}
                                handleDeleteProduct={handleDeleteProduct}
                                editable={editable}
                                brandInfo={row.brandInfo}
                                lastProduct={row.lastProduct}
                                installedTab={installedTab}
                                previousProductManuallyAdded={row.previousProductManuallyAdded}
                                manuallyAddedProductsByBrand={row.manuallyAddedProductsByBrand}
                            />
                        </ProductScrollableContainer>
                    );
                default:
                    return null;
            }
        },
        [
            editable,
            filterModalOpened,
            handleClicked,
            handleDeleteProduct,
            handleSectionOpen,
            handleSelectProduct,
            installedTab,
            projectInfo,
            projectReferences,
            references,
            selectedProducts,
            updateDivisions,
        ],
    );

    const rowRenderer = useCallback(
        ({ index, key, parent, style }: ListRowProps) => {
            const row = rowsRef.current[index];
            return (
                <CellMeasurer
                    cache={cache}
                    columnIndex={0}
                    key={key}
                    parent={parent}
                    rowIndex={index}
                >
                    {({ registerChild, measure }) => (
                        <div ref={registerChild as React.LegacyRef<HTMLDivElement>} style={style}>
                            <MeasuredRow measure={measure}>{renderRowContent(row)}</MeasuredRow>
                        </div>
                    )}
                </CellMeasurer>
            );
        },
        [cache, renderRowContent],
    );

    return (
        <DivisionsContainer className={divisions[0]?.clicked ? 'clicked' : ''}>
            <WindowScroller>
                {({ height, isScrolling, onChildScroll, scrollTop, registerChild }) => (
                    <AutoSizer disableHeight>
                        {({ width }) => (
                            <div ref={registerChild as React.LegacyRef<HTMLDivElement>}>
                                <List
                                    ref={listRef}
                                    autoHeight
                                    height={height}
                                    width={width}
                                    isScrolling={isScrolling}
                                    onScroll={onChildScroll}
                                    scrollTop={scrollTop}
                                    overscanRowCount={4}
                                    rowCount={rows.length}
                                    rowHeight={cache.rowHeight}
                                    deferredMeasurementCache={cache}
                                    rowRenderer={rowRenderer}
                                />
                            </div>
                        )}
                    </AutoSizer>
                )}
            </WindowScroller>
        </DivisionsContainer>
    );
};

export default VirtualizedDivisions;
